import { useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Btn, Chip, Spinner } from "@/components/kit";
import { useAdminInvalidate, useAdminQuery } from "@/hooks/queries";
import { adminApi } from "@/lib/api";
import { fmtDateTime, rupees } from "@/lib/toto";
import { AdminHeader, AdminTable, Badge } from "@/pages/admin/AdminLayout";

const date = (d) => (d ? fmtDateTime(d).split(",").slice(0, 2).join(",") : "—");

function UserSheet({ id, onClose }) {
  const q = useAdminQuery(`/admin/users/${id}`, { enabled: !!id });
  const refresh = useAdminInvalidate();
  const [busy, setBusy] = useState(false);
  const u = q.data;
  const act = async (body, msg) => {
    setBusy(true);
    try { await adminApi.patch(`/admin/users/${id}`, body); toast.success(msg); refresh(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="admin-user-sheet">
        <SheetTitle className="text-[20px] font-extrabold">{u?.name ?? "User"}</SheetTitle>
        <SheetDescription>{u?.email}</SheetDescription>
        {!u ? <Spinner /> : (
          <div className="mt-5 space-y-5 text-[14px]">
            <div className="flex gap-2 flex-wrap"><Badge tone={u.status === "active" ? "success" : "danger"}>{u.status}</Badge><Badge tone={u.plan === "premium" ? "brand" : "neutral"}>{u.plan}</Badge></div>
            <div className="t-card divide-y divide-[#F1F5F9]">
              {[["Phone", u.phone || "—"], ["Joined", date(u.createdAt)], ["Scans used", `${u.scansUsed} / ${u.freeScanLimit ?? "—"}`], ["Successful scans", u.successfulScans ?? "—"], ["Contacts", u.contactsCount],
                ["Subscription", `${u.subscription?.planId ?? u.plan} · ${u.subscription?.status ?? "—"}`], ["Expiry", date(u.subscription?.expiryDate)], ["Provider", u.subscription?.provider || "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-2.5"><span className="text-[#64748B]">{k}</span><span className="font-semibold">{v}</span></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {u.status === "active" ? <Btn variant="danger" className="h-11 text-[14px]" loading={busy} onClick={() => act({ status: "suspended" }, "User suspended.")} testid="admin-suspend">Suspend</Btn>
                : <Btn className="h-11 text-[14px]" loading={busy} onClick={() => act({ status: "active" }, "User activated.")} testid="admin-activate">Activate</Btn>}
              {u.plan === "premium" ? <Btn variant="outline" className="h-11 text-[14px]" loading={busy} onClick={() => act({ plan: "free" }, "Premium revoked.")} testid="admin-revoke">Revoke Premium</Btn>
                : <Btn variant="outline" className="h-11 text-[14px]" loading={busy} onClick={() => act({ plan: "premium" }, "Premium granted.")} testid="admin-grant">Grant Premium</Btn>}
            </div>
            <div>
              <p className="font-bold mb-2">Payments</p>
              {(u.payments ?? []).map((p) => <div key={p.paymentId} className="flex justify-between py-1.5"><span>{date(p.date)}</span><span className="font-semibold">{rupees(p.amountPaise)}</span></div>)}
              {!u.payments?.length && <p className="text-[#64748B]">No payments.</p>}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState(null);
  const p = new URLSearchParams({ page: String(page) });
  if (search.trim()) p.set("search", search.trim());
  if (status) p.set("status", status);
  const q = useAdminQuery(`/admin/users?${p}`);
  const d = q.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / (d.limit || 20))) : 1;
  return (
    <>
      <AdminHeader title="Users" sub={d ? `${d.total} total` : ""} />
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md"><Search className="absolute left-4 top-[15px] w-5 h-5 text-[#64748B]" /><input className="t-input pl-12 bg-white" placeholder="Search name, email, phone" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="admin-users-search" /></div>
        {[["", "All"], ["active", "Active"], ["suspended", "Suspended"]].map(([v, l]) => <Chip key={v} selected={status === v} onClick={() => { setStatus(v); setPage(1); }} testid={`admin-users-status-${v || "all"}`}>{l}</Chip>)}
      </div>
      {q.isLoading ? <Spinner /> : (
        <AdminTable testid="admin-users-table" rows={d?.items ?? []} onRow={(r) => setSel(r.id)} cols={[
          { label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> }, { label: "Email", k: "email" }, { label: "Phone", render: (r) => r.phone || "—" },
          { label: "Plan", render: (r) => <Badge tone={r.plan === "premium" ? "brand" : "neutral"}>{r.plan}</Badge> },
          { label: "Status", render: (r) => <Badge tone={r.status === "active" ? "success" : "danger"}>{r.status}</Badge> },
          { label: "Scans", k: "scansUsed" }, { label: "Contacts", k: "contactsCount" }, { label: "Joined", render: (r) => date(r.createdAt) },
        ]} />
      )}
      <div className="flex items-center gap-3 mt-4 justify-end text-[14px]">
        <Btn variant="ghost" className="h-10" disabled={page <= 1} onClick={() => setPage(page - 1)} testid="admin-users-prev">Previous</Btn>
        <span>Page {page} / {pages}</span>
        <Btn variant="ghost" className="h-10" disabled={page >= pages} onClick={() => setPage(page + 1)} testid="admin-users-next">Next</Btn>
      </div>
      <UserSheet id={sel} onClose={() => setSel(null)} />
    </>
  );
}
